/**
 * WorkspaceMemberService.js
 *
 * Service for reading and managing space members.
 */

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';
import { getPersonEmail, getPersonName } from 'utilities/PersonIdentity';
import { buildUserSummary } from 'utilities/Users';

const WORKSPACES_COLLECTION = collections.workspaces;
const callable = (name) => httpsCallable(functions, name);

function isPlainObject(value) {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== undefined)
      .map((entry) => stripUndefinedDeep(entry));
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined) {
      return accumulator;
    }

    accumulator[key] = stripUndefinedDeep(entryValue);
    return accumulator;
  }, {});
}

function membersDatabase(workspaceKey) {
  return collection(db, WORKSPACES_COLLECTION, workspaceKey, 'members');
}

function memberDocument(workspaceKey, userKey) {
  return doc(db, WORKSPACES_COLLECTION, workspaceKey, 'members', userKey);
}

export function subscribeToWorkspaceMembers(workspaceKey, callback, onError) {
  if (!workspaceKey || typeof callback !== 'function') {
    return () => {};
  }

  const q = query(membersDatabase(workspaceKey));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      callback(rows);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[WorkspaceMemberService] subscribeToWorkspaceMembers error:', error);
      callback([]);
    }
  );
}

export function subscribeToWorkspaceMember(workspaceKey, userKey, callback, onError) {
  const normalizedWorkspaceKey = String(workspaceKey || '').trim();
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedWorkspaceKey || !normalizedUserKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    memberDocument(normalizedWorkspaceKey, normalizedUserKey),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback({
        id: snapshot.id,
        ...(snapshot.data() || {}),
      });
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[WorkspaceMemberService] subscribeToWorkspaceMember error:', error);
      callback(null);
    }
  );
}

export async function upsertWorkspaceMember(workspaceKey, userNode, role = 'member') {
  if (!workspaceKey || !userNode?.key) {
    throw new Error('workspaceKey and user key are required');
  }

  const now = serverTimestamp();
  const userKey = userNode.key;
  const userSummary = buildUserSummary(userNode);

  await setDoc(
    doc(db, WORKSPACES_COLLECTION, workspaceKey, 'members', userKey),
    {
      key: userKey,
      userKey,
      workspaceKey,
      role,
      user: userSummary,
      title: userSummary?.name || getPersonName(userNode),
      email: userSummary?.email || getPersonEmail(userNode),
      dateModified: now,
      dateCreated: now,
    },
    { merge: true }
  );
}

export async function setWorkspaceMemberRole(workspaceKey, userKey, role) {
  if (!workspaceKey || !userKey || !role) {
    throw new Error('workspaceKey, userKey, and role are required');
  }

  const fn = callable('setWorkspaceMemberRole');
  await fn({ workspaceKey, userKey, role });
}

export async function removeWorkspaceMember(workspaceKey, userKey) {
  if (!workspaceKey || !userKey) {
    throw new Error('workspaceKey and userKey are required');
  }

  const fn = callable('removeWorkspaceMember');
  await fn({ workspaceKey, userKey });
}

export async function updateWorkspaceMemberProfileData(workspaceKey, userKey, profileData) {
  const normalizedWorkspaceKey = String(workspaceKey || '').trim();
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedWorkspaceKey || !normalizedUserKey) {
    throw new Error('workspaceKey and userKey are required');
  }

  const normalizedProfileData = profileData && typeof profileData === 'object'
    ? stripUndefinedDeep(profileData)
    : {};

  await updateDoc(memberDocument(normalizedWorkspaceKey, normalizedUserKey), {
    data_member_profile: normalizedProfileData,
    dateModified: serverTimestamp(),
  });
}
